import { humanizeError } from "@/lib/errors";
import { useCallback, useEffect, useState } from "react";

import {
  APIError,
  BUILTIN_TOOLS,
  getBaseTools,
  setBaseTools,
  type BuiltinTools,
} from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/toast";
import { Spinner } from "@/components/Page";

const is403 = (e: unknown) => e instanceof APIError && e.status === 403;

// Display metadata for each built-in tool. `warn` flags a tool whose removal
// is drastic (execute_code disables all code execution + connected servers).
const TOOL_META: Record<string, { label: string; desc: string; warn?: boolean }> = {
  execute_code: {
    label: "Execute code",
    desc: "Run TypeScript with Bun in the sandbox. This is how the agent runs logic and calls connected MCP servers.",
    warn: true,
  },
  read_file: { label: "Read file", desc: "Read files from the agent's workspace (servers/, skills/, state/)." },
  write_file: { label: "Write file", desc: "Save files to the agent's persistent workspace." },
  list_directory: { label: "List directory", desc: "Browse the agent's workspace directories." },
  publish_view: {
    label: "Publish view",
    desc: "Render an interactive dashboard, report or page (a generative-UI view) and share it as a link.",
  },
  ask_human: { label: "Ask a human", desc: "Escalate a question to a human operator when the agent is stuck." },
  get_answer: { label: "Get answer", desc: "Retrieve a human's reply to an earlier ask_human." },
  check_approval: { label: "Check approval", desc: "Poll the status of a policy-gated (HOLD) tool call." },
};

export function BuiltinToolsCard({ projectId }: { projectId: string }) {
  const toast = useToast();
  const [tools, setTools] = useState<BuiltinTools | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    getBaseTools(projectId)
      .then((r) => live && setTools(r.tools))
      .catch((e) => live && setLoadErr(is403(e) ? "You need admin access to view this." : humanizeError(e, "Couldn't load tools")));
    return () => {
      live = false;
    };
  }, [projectId]);

  const onToggle = useCallback(
    async (name: string, next: boolean) => {
      if (!tools) return;
      const prev = tools;
      const updated = { ...tools, [name]: next };
      setTools(updated); // optimistic
      setSaving(true);
      try {
        const r = await setBaseTools(projectId, updated);
        setTools(r.tools);
      } catch (e) {
        setTools(prev); // revert
        toast.error(is403(e) ? "You need admin access to change this." : humanizeError(e, "Couldn't update tools"));
      } finally {
        setSaving(false);
      }
    },
    [tools, projectId, toast],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Built-in tools {saving && <Spinner />}
        </CardTitle>
        <CardDescription>
          Choose which built-in <code>/mcp</code> tools this agent exposes. These are separate from your
          connected servers (managed under Integrations). Disabling a tool hides it from the agent's tool
          list and rejects any call to it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loadErr ? (
          <p className="text-sm text-muted-foreground">{loadErr}</p>
        ) : tools === null ? (
          <Spinner />
        ) : (
          <div className="divide-y divide-border rounded-md border border-border">
            {BUILTIN_TOOLS.map((name) => {
              const meta = TOOL_META[name];
              const enabled = tools[name] ?? true;
              return (
                <label
                  key={name}
                  className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-muted/40"
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    onChange={(e) => onToggle(name, e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-input"
                  />
                  <span className="min-w-0">
                    <span className="flex items-center gap-2 text-sm font-medium">
                      {meta?.label ?? name}
                      <code className="text-xs text-muted-foreground">{name}</code>
                    </span>
                    <span className="block text-xs text-muted-foreground">{meta?.desc}</span>
                    {meta?.warn && !enabled && (
                      <span className="mt-1 block text-xs text-amber-600 dark:text-amber-500">
                        With code execution off, the agent can't run code or reach connected MCP servers.
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
